import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiAlertCircle, FiImage, FiUploadCloud } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { apiUrl, fileUrl } from '../utils/api';
import { buildSafeUploadFileName } from '../utils/fileNames';
import InlineLoader from './InlineLoader';
import './ResultadoDiagnosticoForm.css';
import {
  createBatchEntryFromSample,
  getLatestResultForSample,
  normalizeBatchEntryValue,
  shouldSaveBatchEntry,
} from './resultadoBatchUtils';

const RESULT_ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.webp,.bmp,.gif,.svg,.jfif,.tif,.tiff,.heic,.heif,.avif,.pdf';
const RESULT_REQUIRED_FIELDS = {
  tipo_analisis: 'Tipo de análisis',
  resultado_texto: 'Resultado',
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};

const autoResizeTextarea = (textarea) => {
  if (!textarea) {
    return;
  }

  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const getAttachmentName = (path = '') => {
  if (!path) {
    return 'archivo';
  }

  const cleanPath = path.split('?')[0];
  const segments = cleanPath.split('/');
  return segments[segments.length - 1] || 'archivo';
};

const ResultAttachmentPicker = ({
  compact = false,
  accessibleLabel,
  existingFilePath = '',
  helperText,
  inputId,
  inputKey,
  onChange,
  selectedFileName = '',
}) => (
  <div className={`result-upload-field${compact ? ' result-upload-field--compact' : ''}`}>
    <input
      id={inputId}
      key={inputKey}
      type="file"
      aria-label={accessibleLabel}
      className="result-upload-field__input"
      accept={RESULT_ATTACHMENT_ACCEPT}
      onChange={onChange}
    />
    <label htmlFor={inputId} className="result-upload-field__button">
      <span className="result-upload-field__button-icon" aria-hidden="true">
        <FiImage size={28} />
      </span>
      <span className="result-upload-field__button-copy">
        <strong>{selectedFileName ? 'Cambiar imagen o PDF' : compact ? 'Adjuntar imagen o PDF' : 'Seleccionar imagen o PDF de esta muestra'}</strong>
        {selectedFileName || !compact ? <small>{selectedFileName || 'Haz clic aquí para seleccionar una fotografía o un PDF.'}</small> : null}
      </span>
    </label>
    {helperText ? <div className="form-text mt-2">{helperText}</div> : null}

    {selectedFileName && !compact ? (
      <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
        <FiUploadCloud aria-hidden="true" /> Archivo seleccionado: <strong>{selectedFileName}</strong>.
        {' '}Guarda el resultado para subirlo.
      </div>
    ) : null}

    {existingFilePath ? (
      <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
        Adjunto actual:{' '}
        <a href={fileUrl(existingFilePath)} target="_blank" rel="noreferrer">
          {getAttachmentName(existingFilePath)}
        </a>
      </div>
    ) : null}
  </div>
);

const ResultadoDiagnosticoForm = ({
  protocoloMuestraInicial = '',
  protocoloGeneralInicial = '',
  resultId = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(resultId);
  const returnToCaseDetail = Boolean(location.state?.returnToCaseDetail || isEditMode);
  const [tipoAnalisis, setTipoAnalisis] = useState('');
  const [resultadoTexto, setResultadoTexto] = useState('');
  const [muestras, setMuestras] = useState([]);
  const [protocolos, setProtocolos] = useState([]);
  const [protocoloMuestra, setProtocoloMuestra] = useState(protocoloMuestraInicial);
  const [protocoloSeleccionado, setProtocoloSeleccionado] = useState(protocoloGeneralInicial);
  const [batchEntries, setBatchEntries] = useState({});
  const [loadingProtocols, setLoadingProtocols] = useState(true);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [loadingResult, setLoadingResult] = useState(isEditMode);
  const [savingBatch, setSavingBatch] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [editingResult, setEditingResult] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [batchFocusSample, setBatchFocusSample] = useState(protocoloMuestraInicial);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const validationCardRef = useRef(null);
  const resultadoTextareaRef = useRef(null);

  useEffect(() => {
    const fetchProtocolos = async () => {
      setLoadingProtocols(true);
      try {
        const casosRes = await axios.get(apiUrl('/api/ingresos_casos/?compact=1'));
        setProtocolos(casosRes.data || []);
      } catch (error) {
        toast.error('Error al cargar protocolos');
        console.error('Detalles del error:', error.response?.data || error);
      } finally {
        setLoadingProtocols(false);
      }
    };

    fetchProtocolos();
  }, []);

  useEffect(() => {
    if (!protocoloSeleccionado) {
      setMuestras([]);
      return;
    }

    let active = true;

    const fetchMuestras = async () => {
      setLoadingSamples(true);
      try {
        const response = await axios.get(
          apiUrl(`/api/ingresos_muestras/?protocolo_general=${encodeURIComponent(protocoloSeleccionado)}`)
        );

        if (!active) {
          return;
        }

        setMuestras(response.data || []);
      } catch (error) {
        if (!active) {
          return;
        }

        setMuestras([]);
        toast.error('Error al cargar las muestras del protocolo');
        console.error('Detalles del error:', error.response?.data || error);
      } finally {
        if (active) {
          setLoadingSamples(false);
        }
      }
    };

    fetchMuestras();

    return () => {
      active = false;
    };
  }, [protocoloSeleccionado]);

  useEffect(() => {
    if (!isEditMode) {
      setLoadingResult(false);
      return;
    }

    let active = true;
    setLoadingResult(true);

    axios
      .get(apiUrl(`/api/resultados/${resultId}/`), {
        headers: getAuthHeaders(),
      })
      .then((response) => {
        if (!active) {
          return;
        }

        const result = response.data || {};
        setEditingResult(result);
        setProtocoloMuestra(result.muestra || '');
        setTipoAnalisis(result.tipo_analisis || '');
        setResultadoTexto(result.resultado_texto || '');
        setSelectedAttachment(null);
      })
      .catch((error) => {
        console.error('Error al cargar el resultado:', error);
        if (!active) {
          return;
        }

        const message = 'No se pudo cargar el resultado para editar.';
        setSaveNotice({ type: 'error', text: message });
        toast.error(message);
      })
      .finally(() => {
        if (active) {
          setLoadingResult(false);
        }
      });

    return () => {
      active = false;
    };
  }, [isEditMode, resultId]);

  useEffect(() => {
    if (isEditMode && editingResult && muestras.length > 0) {
      const muestraEditada = muestras.find((muestra) => muestra.protocolo_muestra === editingResult.muestra);
      if (muestraEditada) {
        setProtocoloSeleccionado(muestraEditada.protocolo_general || '');
      }
      return;
    }

    if (protocoloGeneralInicial) {
      setProtocoloSeleccionado(protocoloGeneralInicial);
    }
  }, [editingResult, isEditMode, muestras, protocoloGeneralInicial]);

  useEffect(() => {
    if (!protocoloMuestraInicial || isEditMode || !muestras.length) {
      return;
    }

    const muestraInicial = muestras.find((muestra) => muestra.protocolo_muestra === protocoloMuestraInicial);
    if (!muestraInicial) {
      return;
    }

    setProtocoloMuestra(muestraInicial.protocolo_muestra);
    setProtocoloSeleccionado(muestraInicial.protocolo_general || '');
    setBatchFocusSample(muestraInicial.protocolo_muestra);
  }, [isEditMode, muestras, protocoloMuestraInicial]);

  const muestrasFiltradas = useMemo(
    () => muestras.filter((muestra) => muestra.protocolo_general === protocoloSeleccionado),
    [muestras, protocoloSeleccionado]
  );

  const muestraSeleccionada = useMemo(
    () => muestras.find((muestra) => muestra.protocolo_muestra === protocoloMuestra),
    [muestras, protocoloMuestra]
  );

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    setBatchEntries((current) => {
      const nextEntries = {};

      muestrasFiltradas.forEach((muestra) => {
        nextEntries[muestra.protocolo_muestra] = current[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
      });

      return nextEntries;
    });
  }, [isEditMode, muestrasFiltradas]);

  useEffect(() => {
    if (isEditMode) {
      autoResizeTextarea(resultadoTextareaRef.current);
    }
  }, [isEditMode, resultadoTexto]);

  const sectorMuestraSeleccionada = muestraSeleccionada?.sector || '';
  const singleAttachmentInputId = `result-attachment-${resultId || protocoloMuestra || protocoloGeneralInicial || 'nuevo'}`;

  const muestrasConResultados = useMemo(
    () => muestrasFiltradas.filter((muestra) => Array.isArray(muestra.resultados) && muestra.resultados.length > 0).length,
    [muestrasFiltradas]
  );

  const muestrasCompletadas = useMemo(
    () =>
      muestrasFiltradas.filter((muestra) => {
        const entry = batchEntries[muestra.protocolo_muestra];
        return shouldSaveBatchEntry(muestra, entry);
      }).length,
    [batchEntries, muestrasFiltradas]
  );

  const batchErrors = useMemo(() => {
    const errors = {};
    muestrasFiltradas.forEach((muestra) => {
      const entry = batchEntries[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
      const fields = Object.keys(RESULT_REQUIRED_FIELDS);
      const hasContent = fields.some((field) => normalizeBatchEntryValue(entry[field])) || entry.archivo_resultado;
      if (!hasContent) return;
      const missingFields = fields.filter((field) => !normalizeBatchEntryValue(entry[field]));
      if (missingFields.length) errors[muestra.protocolo_muestra] = missingFields;
    });
    return errors;
  }, [batchEntries, muestrasFiltradas]);
  const visibleBatchErrors = validationAttempt > 0 ? batchErrors : {};
  const hasValidationErrors = Object.keys(visibleBatchErrors).length > 0;

  useEffect(() => {
    if (validationAttempt > 0) {
      validationCardRef.current?.focus();
      validationCardRef.current?.scrollIntoView?.({ block: 'center' });
    }
  }, [validationAttempt]);

  const handleProtocoloChange = (event) => {
    const nuevoProtocolo = event.target.value;
    setProtocoloSeleccionado(nuevoProtocolo);
    setProtocoloMuestra('');
    setSaveNotice(null);
    setValidationAttempt(0);
  };

  const handleBatchEntryChange = (protocolo, field, value) => {
    setBatchEntries((current) => ({
      ...current,
      [protocolo]: {
        ...(current[protocolo] || createBatchEntryFromSample(muestras.find((muestra) => muestra.protocolo_muestra === protocolo))),
        [field]: value,
      },
    }));
  };

  const handleSingleAttachmentChange = (event) => {
    setSelectedAttachment(event.target.files?.[0] || null);
  };

  const handleSingleSubmit = async (event) => {
    event.preventDefault();
    setSaveNotice(null);

    if (!protocoloMuestra) {
      toast.error('Debes seleccionar una muestra');
      setSaveNotice({ type: 'error', text: 'Debes seleccionar una muestra antes de guardar.' });
      return;
    }

    if (!sectorMuestraSeleccionada) {
      toast.error('La muestra seleccionada no tiene sector asignado.');
      setSaveNotice({
        type: 'error',
        text: 'La muestra seleccionada no tiene sector asignado. Revisa la carga de la muestra antes de guardar el resultado.',
      });
      return;
    }

    const formData = new FormData();
    formData.append('muestra', protocoloMuestra);
    formData.append('sector', sectorMuestraSeleccionada);
    formData.append('tipo_analisis', tipoAnalisis);
    formData.append('resultado_texto', resultadoTexto);
    if (selectedAttachment) {
      formData.append('archivo_resultado', selectedAttachment, buildSafeUploadFileName(selectedAttachment.name));
    }

    try {
      const response = isEditMode
        ? await axios.put(apiUrl(`/api/resultados/${resultId}/`), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...getAuthHeaders(),
            },
          })
        : await axios.post(apiUrl('/api/resultados/'), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...getAuthHeaders(),
            },
          });

      const successText = isEditMode ? 'Resultado actualizado correctamente.' : 'Resultado guardado correctamente.';
      const resultIdentifier = response.data?.id || resultId;

      toast.success(successText);
      setSaveNotice({ type: 'success', text: successText });
      setSelectedAttachment(null);

      if (protocoloSeleccionado && returnToCaseDetail) {
        navigate(`/ver-caso/${protocoloSeleccionado}`, {
          state: isEditMode
            ? { muestraActiva: protocoloMuestra, resultadoActivo: resultIdentifier, resultadoActualizado: resultIdentifier }
            : { muestraActiva: protocoloMuestra, resultadoActivo: resultIdentifier, resultadoRecienCreado: resultIdentifier },
        });
        return;
      }

      if (!isEditMode) {
        setTipoAnalisis('');
        setResultadoTexto('');
        setProtocoloMuestra('');
      }
    } catch (error) {
      toast.error(isEditMode ? 'Error al actualizar el resultado' : 'Error al guardar el resultado');
      console.error('Detalles del error:', error.response?.data || error);
      setSaveNotice({
        type: 'error',
        text: isEditMode
          ? 'No se pudo actualizar el resultado. Revisa los datos e intenta nuevamente.'
          : 'No se pudo guardar el resultado. Revisa los datos e intenta nuevamente.',
      });
    }
  };

  const handleBatchSubmit = async (event) => {
    event.preventDefault();
    setSaveNotice(null);

    if (!protocoloSeleccionado) {
      const message = 'Selecciona un protocolo general antes de guardar resultados.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    if (muestrasFiltradas.length === 0) {
      const message = 'El protocolo seleccionado no tiene muestras para cargar.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    if (Object.keys(batchErrors).length > 0) {
      setValidationAttempt((attempt) => attempt + 1);
      return;
    }

    const muestrasParaGuardar = muestrasFiltradas.filter((muestra) => {
      const entry = batchEntries[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
      return shouldSaveBatchEntry(muestra, entry);
    });

    if (muestrasParaGuardar.length === 0) {
      const message = 'Modifica un resultado precargado, adjunta un archivo o completa una muestra sin resultados antes de guardar.';
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    const muestrasSinSector = muestrasParaGuardar.filter((muestra) => !muestra.sector);
    if (muestrasSinSector.length > 0) {
      const message = `Estas muestras no tienen sector asignado: ${muestrasSinSector
        .map((muestra) => muestra.protocolo_muestra)
        .join(', ')}.`;
      toast.error(message);
      setSaveNotice({ type: 'error', text: message });
      return;
    }

    setSavingBatch(true);

    try {
      const responses = await Promise.allSettled(
        muestrasParaGuardar.map(async (muestra) => {
          const entry = batchEntries[muestra.protocolo_muestra];
          const formData = new FormData();
          formData.append('muestra', muestra.protocolo_muestra);
          formData.append('sector', muestra.sector || '');
          formData.append('tipo_analisis', normalizeBatchEntryValue(entry?.tipo_analisis));
          formData.append('resultado_texto', normalizeBatchEntryValue(entry?.resultado_texto));
          if (entry?.archivo_resultado) {
            formData.append(
              'archivo_resultado',
              entry.archivo_resultado,
              buildSafeUploadFileName(entry.archivo_resultado.name)
            );
          }

          const response = await axios.post(apiUrl('/api/resultados/'), formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...getAuthHeaders(),
            },
          });

          return {
            muestra,
            data: response.data,
          };
        })
      );

      const exitosos = responses
        .filter((item) => item.status === 'fulfilled')
        .map((item) => item.value);
      const fallidos = responses
        .map((item, index) => (item.status === 'rejected' ? muestrasParaGuardar[index].protocolo_muestra : null))
        .filter(Boolean);

      if (exitosos.length > 0) {
        setMuestras((current) =>
          current.map((muestra) => {
            const match = exitosos.find((item) => item.muestra.protocolo_muestra === muestra.protocolo_muestra);
            if (!match) {
              return muestra;
            }

            return {
              ...muestra,
              resultados: [...(muestra.resultados || []), match.data],
            };
          })
        );

        setBatchEntries((current) => {
          const next = { ...current };
          exitosos.forEach(({ muestra }) => {
            const muestraActualizada = {
              ...muestra,
              resultados: [...(muestra.resultados || []), exitosos.find((item) => item.muestra.protocolo_muestra === muestra.protocolo_muestra)?.data].filter(Boolean),
            };
            next[muestra.protocolo_muestra] = createBatchEntryFromSample(muestraActualizada);
          });
          return next;
        });
      }

      if (fallidos.length === 0) {
        setValidationAttempt(0);
        const message =
          exitosos.length === 1
            ? 'Se guardó 1 resultado correctamente.'
            : `Se guardaron ${exitosos.length} resultados correctamente.`;
        toast.success(message);
        setSaveNotice({ type: 'success', text: message });

        if (returnToCaseDetail) {
          navigate(`/ver-caso/${protocoloSeleccionado}`, {
            state: {
              muestraActiva: exitosos[0]?.muestra?.protocolo_muestra || batchFocusSample || '',
              resultadoActivo: exitosos[0]?.data?.id || '',
              resultadosCreadosCantidad: exitosos.length,
            },
          });
        }
        return;
      }

      const message =
        exitosos.length > 0
          ? `Se guardaron ${exitosos.length} resultados, pero fallaron estas muestras: ${fallidos.join(', ')}.`
          : `No se pudo guardar ningún resultado. Revisa estas muestras: ${fallidos.join(', ')}.`;
      toast[exitosos.length > 0 ? 'warn' : 'error'](message);
      setSaveNotice({ type: 'error', text: message });
    } catch (error) {
      toast.error('No se pudieron guardar los resultados.');
      console.error('Detalles del error:', error.response?.data || error);
      setSaveNotice({
        type: 'error',
        text: 'No se pudieron guardar los resultados. Revisa los datos e intenta nuevamente.',
      });
    } finally {
      setSavingBatch(false);
    }
  };

  if (loadingProtocols || loadingResult) {
    return (
      <div className="surface-card">
        <InlineLoader label={loadingResult ? 'Cargando resultado para editar...' : 'Cargando protocolos...'} />
      </div>
    );
  }

  if (isEditMode) {
    return (
      <div className="surface-card">
        <div className="form-highlight mb-4">
          <div>
            <span className="section-kicker">Edición</span>
            <h3 className="form-title">Editar resultado por muestra</h3>
            <p className="page-subtitle mt-2 mb-0">
              Ajusta el análisis y regresa al detalle del caso para verificar el cambio.
            </p>
          </div>
          <div className="protocol-chip">
            {protocoloMuestra ? `Muestra ${protocoloMuestra}` : 'Pendiente de selección'}
          </div>
        </div>

        <div className="result-form-grid mb-4">
          <div className="result-form-grid__main">
            <form onSubmit={handleSingleSubmit}>
              {saveNotice ? (
                <div className={`alert ${saveNotice.type === 'success' ? 'alert-success' : 'subtle-alert'} mb-4`} role="status">
                  {saveNotice.text}
                </div>
              ) : null}

              <div className="mb-3">
                <label className="form-label">Protocolo general</label>
                <select className="form-select" value={protocoloSeleccionado} onChange={handleProtocoloChange} required>
                  <option value="">Seleccione un protocolo...</option>
                  {protocolos.map((protocolo) => (
                    <option key={protocolo.protocolo_general} value={protocolo.protocolo_general}>
                      {protocolo.protocolo_general}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Muestra</label>
                <select
                  className="form-select"
                  value={protocoloMuestra}
                  onChange={(event) => setProtocoloMuestra(event.target.value)}
                  disabled={!protocoloSeleccionado}
                  required
                >
                  <option value="">
                    {protocoloSeleccionado ? 'Seleccione una muestra...' : 'Primero elige un protocolo'}
                  </option>
                  {muestrasFiltradas.map((muestra) => (
                    <option key={muestra.protocolo_muestra} value={muestra.protocolo_muestra}>
                      {muestra.protocolo_muestra}
                    </option>
                  ))}
                </select>
                <div className="form-text mt-2">
                  Solo se muestran las muestras asociadas al protocolo general seleccionado.
                </div>
              </div>

              {loadingSamples ? (
                <div className="mb-4">
                  <InlineLoader label="Cargando muestras del protocolo..." />
                </div>
              ) : null}

              {protocoloSeleccionado && !loadingSamples && muestrasFiltradas.length === 0 ? (
                <div className="alert subtle-alert d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                  <div>
                    No hay muestras asociadas al protocolo <strong>{protocoloSeleccionado}</strong>.
                    Registra una muestra para poder cargar el resultado diagnóstico.
                  </div>
                  <Link
                    to="/ingreso-muestra"
                    state={{ protocoloGeneral: protocoloSeleccionado, returnToCaseDetail: true }}
                    className="btn btn-outline-brand"
                  >
                    Agregar muestra
                  </Link>
                </div>
              ) : null}

              <div className="mb-3">
                <label className="form-label">Sector</label>
                <div className="readonly-field">
                  <strong className="readonly-field__value">
                    {sectorMuestraSeleccionada || 'Selecciona una muestra para ver su sector'}
                  </strong>
                </div>
                <div className="form-text mt-2">
                  El sector se toma automáticamente desde la muestra seleccionada.
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Tipo de análisis</label>
                <input
                  type="text"
                  className="form-control"
                  value={tipoAnalisis}
                  onChange={(event) => setTipoAnalisis(event.target.value)}
                  placeholder="Ej. Cultivo bacteriológico"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Resultado</label>
                <textarea
                  ref={resultadoTextareaRef}
                  className="form-control result-form__textarea"
                  value={resultadoTexto}
                  onChange={(event) => setResultadoTexto(event.target.value)}
                  rows="7"
                  placeholder="Resume hallazgos, conclusión e información relevante específicamente sobre la muestra tomada."
                  required
                />
              </div>

              <div className="mb-4">
                <label className="form-label">Adjunto del resultado</label>
                <ResultAttachmentPicker
                  existingFilePath={editingResult?.archivo_adjunto || ''}
                  helperText="Puedes adjuntar una imagen diagnostica o un PDF complementario para este resultado."
                  inputId={singleAttachmentInputId}
                  inputKey={selectedAttachment?.name || editingResult?.archivo_adjunto || 'sin-adjunto'}
                  onChange={handleSingleAttachmentChange}
                  selectedFileName={selectedAttachment?.name || ''}
                />
              </div>

              <div className="d-flex justify-content-end pt-2">
                <button type="submit" className="btn btn-brand">
                  Guardar cambios del resultado
                </button>
              </div>
            </form>
          </div>

          <aside className="result-form-grid__aside">
            <div className="result-summary-card">
              <span className="section-kicker">Resumen</span>
              <h4 className="result-summary-card__title">Antes de guardar</h4>
              <p className="result-summary-card__text">
                Revisa que el resultado quede asociado a la muestra correcta para evitar cargas duplicadas o mal vinculadas.
              </p>

              <dl className="result-summary-list">
                <div>
                  <dt>Protocolo general</dt>
                  <dd>{protocoloSeleccionado || 'Sin seleccionar'}</dd>
                </div>
                <div>
                  <dt>Muestra</dt>
                  <dd>{protocoloMuestra || 'Sin seleccionar'}</dd>
                </div>
                <div>
                  <dt>Sector</dt>
                  <dd>{sectorMuestraSeleccionada || 'Pendiente'}</dd>
                </div>
                <div>
                  <dt>Tipo de análisis</dt>
                  <dd>{tipoAnalisis || 'Pendiente'}</dd>
                </div>
              </dl>

              {muestraSeleccionada ? (
                <div className="alert subtle-alert subtle-alert--info mt-3 mb-0">
                  La muestra <strong>{muestraSeleccionada.protocolo_muestra}</strong> pertenece al protocolo{' '}
                  <strong>{muestraSeleccionada.protocolo_general}</strong>.
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card">
      <div className="form-highlight mb-4">
        <div>
          <span className="section-kicker">Paso 3</span>
          <h3 className="form-title">Registrar resultados por muestra</h3>
          <p className="page-subtitle mt-2 mb-0">
            Selecciona un protocolo general y completa el análisis, resultado y adjunto de cada muestra en su fila.
          </p>
        </div>
        <div className="protocol-chip">
          {protocoloSeleccionado ? `Caso ${protocoloSeleccionado}` : 'Sin caso seleccionado'}
        </div>
      </div>

      {muestrasFiltradas.some((muestra) => muestra.pack_toro) && <div className="alert subtle-alert mb-3">
        Esta pantalla registra resultados de otros estudios. Para BPA, FPA, ELISA y qPCR del pack, usa{' '}
        <Link to={`/pack-toro/${protocoloSeleccionado}?tab=resultados`}>Cargar resultados Pack Toro</Link>.
      </div>}
      {hasValidationErrors || saveNotice?.type === 'error' ? (
        <section
          ref={validationCardRef}
          className="result-validation-card mb-4"
          role="alert"
          tabIndex={-1}
          aria-labelledby="result-validation-title"
        >
          <h4 id="result-validation-title" className="result-validation-card__title">
            <FiAlertCircle aria-hidden="true" />
            {hasValidationErrors ? 'Revisa los campos pendientes' : 'No se completó el guardado'}
          </h4>
          {hasValidationErrors ? (
            <>
              <p>Completa los campos marcados en rojo. Puedes seleccionar cada aviso para ir al campo.</p>
              <ul className="result-validation-card__list">
                {Object.entries(visibleBatchErrors).flatMap(([protocol, fields]) => fields.map((field) => (
                  <li key={`${protocol}-${field}`}>
                    <button
                      type="button"
                      className="result-validation-card__link"
                      onClick={() => {
                        const input = document.getElementById(`batch-${protocol}-${field}`);
                        input?.focus();
                        input?.scrollIntoView?.({ block: 'center' });
                      }}
                    >
                      <strong>{protocol}:</strong> completa {RESULT_REQUIRED_FIELDS[field].toLowerCase()}.
                    </button>
                  </li>
                )))}
              </ul>
            </>
          ) : <p className="mb-0">{saveNotice.text}</p>}
        </section>
      ) : null}

      <div className="result-batch-layout mb-4">
        <div className="result-form-grid__main">
          <form onSubmit={handleBatchSubmit}>
            {saveNotice?.type === 'success' && !hasValidationErrors ? (
              <div className="alert alert-success mb-4" role="status">
                {saveNotice.text}
              </div>
            ) : null}

            <div className="mb-4">
              <label className="form-label" htmlFor="result-batch-protocol">Protocolo general</label>
              <select id="result-batch-protocol" className="form-select" value={protocoloSeleccionado} onChange={handleProtocoloChange} required>
                <option value="">Seleccione un protocolo...</option>
                {protocolos.map((protocolo) => (
                  <option key={protocolo.protocolo_general} value={protocolo.protocolo_general}>
                    {protocolo.protocolo_general}
                  </option>
                ))}
              </select>
              <div className="form-text mt-2">
                Al elegir un protocolo se muestran todas las muestras asociadas para cargar sus resultados en la misma pantalla.
              </div>
            </div>

            {loadingSamples ? (
              <div className="mb-4">
                <InlineLoader label="Cargando muestras del protocolo..." />
              </div>
            ) : null}

            {protocoloSeleccionado && !loadingSamples && muestrasFiltradas.length === 0 ? (
              <div className="alert subtle-alert d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                <div>
                  No hay muestras asociadas al protocolo <strong>{protocoloSeleccionado}</strong>.
                  Registra una muestra para poder cargar resultados diagnósticos.
                </div>
                <Link
                  to="/ingreso-muestra"
                  state={{ protocoloGeneral: protocoloSeleccionado, returnToCaseDetail: true }}
                  className="btn btn-outline-brand"
                >
                  Agregar muestra
                </Link>
              </div>
            ) : null}

            {!loadingSamples && muestrasFiltradas.length > 0 ? (
              <>
              <div className="result-batch-summary">
                <span><strong>{muestrasFiltradas.length}</strong> muestra(s)</span>
                <span><strong>{muestrasCompletadas}</strong> resultado(s) listo(s) para guardar</span>
                <span><strong>{muestrasConResultados}</strong> con resultados previos</span>
              </div>
              <p id="result-batch-help" className="text-muted small">
                Puedes completar una o varias filas y guardarlas juntas. Los resultados previos se precargan; solo se guardan las filas modificadas.
                {' '}Desplaza la tabla horizontalmente para ver todas las columnas.
              </p>
              <div className="table-responsive result-batch-scroll" tabIndex={0} role="region" aria-label="Tabla de carga de resultados" aria-describedby="result-batch-help">
                <table className="table table-bordered align-top result-batch-table" aria-label="Resultados por muestra">
                  <thead><tr>
                    {['Sector', 'Protocolo de muestra', 'Datos de la muestra', 'Tipo de análisis', 'Resultado', 'Adjunto'].map((label) => <th scope="col" key={label}>{label}</th>)}
                  </tr></thead>
                  <tbody>
                {muestrasFiltradas.map((muestra) => {
                  const entry = batchEntries[muestra.protocolo_muestra] || createBatchEntryFromSample(muestra);
                  const hasExistingResults = Boolean(muestra.resultados?.length);
                  const latestResult = getLatestResultForSample(muestra);
                  const isHighlighted = batchFocusSample && muestra.protocolo_muestra === batchFocusSample;
                  const attachmentInputId = `batch-result-attachment-${muestra.protocolo_muestra}`;
                  const missingFields = visibleBatchErrors[muestra.protocolo_muestra] || [];
                  const typeInputId = `batch-${muestra.protocolo_muestra}-tipo_analisis`;
                  const resultInputId = `batch-${muestra.protocolo_muestra}-resultado_texto`;
                  const typeInvalid = missingFields.includes('tipo_analisis');
                  const resultInvalid = missingFields.includes('resultado_texto');

                  return (
                    <tr
                      key={muestra.protocolo_muestra}
                      className={`${missingFields.length ? 'table-danger' : isHighlighted ? 'table-primary' : hasExistingResults ? 'table-success' : ''}`}
                    >
                      <td>{muestra.sector || 'Sin sector'}</td>
                      <th scope="row">
                        <strong className="d-block">{muestra.protocolo_muestra}</strong>
                        <span className="d-block small fw-normal mt-1">
                          {hasExistingResults ? `${muestra.resultados.length} resultado(s) previos` : 'Sin resultados'}
                        </span>
                        {isHighlighted ? <span className="d-block small mt-1">Muestra seleccionada</span> : null}
                      </th>
                      <td>
                        <strong className="d-block">{muestra.tipo_muestra || 'Sin tipo de muestra'}</strong>
                        <span className="d-block small">{muestra.grupo_zootecnico || muestra.descripcion_vegetal || muestra.tipo_muestra_vegetal || muestra.otro_tipo_muestra || 'Sin grupo / detalle'}</span>
                        {muestra.identificacion ? <span className="d-block small mt-1">Identificación: {muestra.identificacion}</span> : null}
                        {muestra.observaciones ? <details className="mt-2"><summary>Observaciones</summary><p className="small mt-2 mb-0">{muestra.observaciones}</p></details> : null}
                      </td>
                      <td>
                            <input
                              id={typeInputId}
                              aria-label={`Tipo de análisis de ${muestra.protocolo_muestra}`}
                              aria-invalid={typeInvalid}
                              aria-describedby={typeInvalid ? `${typeInputId}-error` : undefined}
                              type="text"
                              className={`form-control${typeInvalid ? ' is-invalid' : ''}`}
                              value={entry.tipo_analisis}
                              onChange={(event) =>
                                handleBatchEntryChange(muestra.protocolo_muestra, 'tipo_analisis', event.target.value)
                              }
                              placeholder="Ej. Cultivo bacteriológico"
                            />
                            {typeInvalid ? <div id={`${typeInputId}-error`} className="invalid-feedback">Completa el tipo de análisis.</div> : null}
                      </td>
                      <td>
                            <textarea
                              id={resultInputId}
                              aria-label={`Resultado de ${muestra.protocolo_muestra}`}
                              aria-invalid={resultInvalid}
                              aria-describedby={resultInvalid ? `${resultInputId}-error` : undefined}
                              className={`form-control result-form__textarea${resultInvalid ? ' is-invalid' : ''}`}
                              value={entry.resultado_texto}
                              onChange={(event) =>
                                handleBatchEntryChange(muestra.protocolo_muestra, 'resultado_texto', event.target.value)
                              }
                              onInput={(event) => autoResizeTextarea(event.currentTarget)}
                              rows="3"
                              placeholder={`Carga el resultado para la muestra ${muestra.protocolo_muestra}.`}
                            />
                            {resultInvalid ? <div id={`${resultInputId}-error`} className="invalid-feedback">Completa el resultado de esta muestra.</div> : null}
                      </td>
                      <td>
                            <ResultAttachmentPicker
                              compact
                              accessibleLabel={`Seleccionar imagen o PDF de esta muestra ${muestra.protocolo_muestra}`}
                              existingFilePath={latestResult?.archivo_adjunto || ''}
                              inputId={attachmentInputId}
                              inputKey={`${muestra.protocolo_muestra}-${entry.archivo_resultado?.name || latestResult?.id || 'sin-adjunto'}`}
                              onChange={(event) =>
                                handleBatchEntryChange(
                                  muestra.protocolo_muestra,
                                  'archivo_resultado',
                                  event.target.files?.[0] || null
                                )
                              }
                              selectedFileName={entry.archivo_resultado?.name || ''}
                            />
                      </td>
                    </tr>
                  );
                })}
                  </tbody>
                </table>
              </div>
              </>
            ) : null}

            <div className="d-flex justify-content-end pt-3">
              <button type="submit" className="btn btn-brand" disabled={savingBatch || loadingSamples || !protocoloSeleccionado}>
                {savingBatch ? 'Guardando resultados...' : 'Guardar resultados cargados'}
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

export default ResultadoDiagnosticoForm;
